/* eslint-disable react/prop-types */
import React, { useContext } from 'react';
import styled from 'styled-components';
import sortBy from 'lodash/sortBy';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableColumn,
  TableHeaderColumn,
} from '@alefeducation/ui-elements-library-core';
import { ProgressCircleHalf } from '@alefeducation/ui-elements-library-icons';
import AssignmentContext from '../../../common/AssignmentContext/AssignmentContext';
import {
  isNcleAlefAssignment,
  getAssignAllTooltip,
  getAssignTooltip,
} from '../../../../utils/isNcleAlefAssignment';
import { filterBySelectedTeam } from '../../helpers';
import { renderAssignAllIcon, renderAssignIcon } from '../AssignStatusIcon';
import { useStudentListContext } from '../StudentListContext';
import { useUpdateAssignStatusToAssignment } from '../useUpdateAssignStatusToAssignment';
import AssignedContentStatus from '../AssignedContentStatus';
import { NewStudentListSection } from './StudentListSection/NewStudentListSection';
import './assignment-student-list.scss';
import { emptyColumn, studentColumn } from './commonStudentSectionColumns';
import { actionColumnWidth } from './constants';

export const NewUnassignedSection = () => {
  const { t } = useTranslation();

  let {
    unAssignedStudents,
    // eslint-disable-next-line prefer-const
    isLockingOfAssignmentEnabled,
  } = useStudentListContext();
  const {
    assignmentParams: { isNcle },
    assignmentData: { assignmentType },
    filters: { selectedTeamMembersIds, selectedTeamFilter },
  } = useContext(AssignmentContext);

  const isNCLE = isNcleAlefAssignment(isNcle, assignmentType);
  const sectionNameLabel = isNCLE ? 'Unassigned' : 'Locked';
  const assignTip = getAssignTooltip(isNCLE);
  const assignAllTip = getAssignAllTooltip(isNCLE);
  const {
    onUpdateAssignStatusStudents,
    updatingStatusInProgress,
  } = useUpdateAssignStatusToAssignment();

  unAssignedStudents =
    unAssignedStudents &&
    filterBySelectedTeam(
      selectedTeamFilter,
      selectedTeamMembersIds,
      unAssignedStudents.map(student => ({ student })),
    );

  const sectionItems =
    unAssignedStudents &&
    sortBy(unAssignedStudents, ({ student }) => student.name).map(
      ({ student }) => student,
    );

  const sectionItemsTable =
    unAssignedStudents.length > 0 ? (
      <Table
        rows={sectionItems}
        columns={[
          studentColumn(t),
          emptyColumn('810px'),
          {
            key: 'id',
            name: 'Actions',
            renderColumn: ({ row }) => (
              <TableColumn
                row={row}
                isFirstColumn={false}
                className="actions-cell"
              >
                <div className="actions-container">
                  <LockButtonWrapper
                    onClick={onUpdateAssignStatusStudents(
                      [row],
                      AssignedContentStatus.Assigned,
                      true,
                    )}
                    data-testid={`assign-student-${row.id}`}
                    data-tip={t(assignTip)}
                    className={`data-analytics-${assignmentType
                      .split('_')
                      .join('-')
                      .toLowerCase()}-class-dashboard-locked-section-single-lock`}
                  >
                    {updatingStatusInProgress ? (
                      <ProgressCircleHalf fontSize={18} />
                    ) : (
                      renderAssignIcon(isNCLE)
                    )}
                  </LockButtonWrapper>
                </div>
              </TableColumn>
            ),
            renderHeaderColumn: rows => (
              <TableHeaderColumn
                isFirstColumn={false}
                rows={rows}
                width={actionColumnWidth}
              >
                <div className="col-heading">{t('Status')}</div>
              </TableHeaderColumn>
            ),
            alignText: 'center',
            width: actionColumnWidth,
            height: '44px',
          },
        ]}
        isSticky={true}
        translationKeys={t}
        sortable={true}
      />
    ) : null;

  return (
    <NewStudentListSection
      data-testid="unassigned-section"
      customCount={`(${unAssignedStudents.length})`}
      submissions={unAssignedStudents}
      sectionName={t(sectionNameLabel)}
      labelColor={'#ffffff'}
      groupControlItems={
        isLockingOfAssignmentEnabled &&
        unAssignedStudents &&
        unAssignedStudents.length ? (
          <span
            onClick={onUpdateAssignStatusStudents(
              unAssignedStudents.map(({ student }) => student),
              AssignedContentStatus.Assigned,
            )}
            data-testid="assign-all-button"
            data-tip={t(assignAllTip)}
            style={{ display: 'flex' }}
            className={`data-analytics-${assignmentType
              .split('_')
              .join('-')
              .toLowerCase()}-class-dashboard-locked-section-group-lock`}
          >
            {updatingStatusInProgress ? (
              <ProgressCircleHalf fontSize={18} />
            ) : (
              renderAssignAllIcon(isNCLE)
            )}
          </span>
        ) : null
      }
      sectionItems={sectionItemsTable}
    />
  );
};

const LockButtonWrapper = styled.div`
  border-radius: 0;
  width: 82px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: flex-end;

  &:hover {
    cursor: pointer;
  }
`;
